import type { ReactNode } from "react";

import { Avatar } from "@/components/avatar";
import { Badge } from "@/components/ui/badge";
import type { TranslationKey } from "@/constants/i18n";
import { useLocaleStore, useTranslation } from "@/store/locale";

/** What the profile page and the employee view both know about an account. */
export interface AccountLike {
  user_name: string;
  full_name: string;
  avatar?: string | null;
  email_address: string | null;
  status: "active" | "password_expired" | "account_locked" | "inactive" | "deleted";
  is_superuser: boolean;
  is_password_request: boolean;
  request_reason?: string | null;
  password_request_at?: string | null;
  force_password_change?: boolean;
  last_password_change_at?: string | null;
  last_login_at?: string | null;
  club: { club_name: string; club_code?: string } | null;
  is_temporary_club: boolean;
  temporary_club: { club_name: string; club_code?: string } | null;
  temporary_club_reason: string | null;
  temporary_start_date: string | null;
  temporary_end_date: string | null;
  has_active_temporary_club: boolean;
  groups: { name: string }[];
  permissions?: string[];
  created_at: string;
  updated_at: string;
}

export const accountName = (account: AccountLike) => account.full_name || account.user_name;

export function useAccountRoles(account: AccountLike): string {
  const t = useTranslation();
  return account.is_superuser
    ? t("shell.roleSuperuser")
    : account.groups.map((group) => group.name).join(", ") || t("shell.roleMember");
}

/** Picture, name, employee number and the three pills; slots for the picture control and actions. */
export function AccountBand({
  account,
  avatar,
  actions,
}: {
  account: AccountLike;
  avatar?: ReactNode;
  actions?: ReactNode;
}) {
  const t = useTranslation();
  const roles = useAccountRoles(account);
  const name = accountName(account);
  return (
    <div className="flex flex-wrap items-center gap-5 p-5 sm:p-6">
      {avatar ?? (
        <Avatar
          src={account.avatar ?? null}
          name={name}
          className="size-20 rounded-[18px] text-2xl"
        />
      )}
      <div className="min-w-0 flex-1">
        <h2 className="text-title-2 text-heading truncate">{name}</h2>
        <p className="text-fg-muted text-callout mt-0.5 truncate">{account.user_name}</p>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <StatusPill status={account.status} />
          <Badge tone="neutral">{roles}</Badge>
          {account.has_active_temporary_club && account.temporary_club ? (
            <Badge tone="elevated">
              {t("profile.postingClub")} {account.temporary_club.club_name}
            </Badge>
          ) : null}
        </div>
      </div>
      {actions ? (
        <div className="flex flex-wrap items-center gap-2 sm:ml-auto">{actions}</div>
      ) : null}
    </div>
  );
}

/** The three groups of facts: account, temporary posting, security. `extra` rows join the account group. */
export function AccountSections({
  account,
  extra = [],
}: {
  account: AccountLike;
  extra?: { label: string; value: ReactNode; muted?: boolean }[];
}) {
  const t = useTranslation();
  const locale = useLocaleStore((state) => state.locale);
  const roles = useAccountRoles(account);
  const when = (value: string | null | undefined) =>
    value
      ? new Date(value).toLocaleString(locale, { dateStyle: "medium", timeStyle: "short" })
      : t("profile.never");
  const day = (value: string | null) =>
    value ? new Date(value).toLocaleDateString(locale, { dateStyle: "medium" }) : "—";
  const clubLabel = (club: AccountLike["club"]) =>
    club ? (club.club_code ? `${club.club_name} (${club.club_code})` : club.club_name) : null;

  return (
    <>
      <Section title={t("profile.sectionAccount")}>
        <Row label={t("profile.fullName")} value={account.full_name} />
        <Row label={t("profile.employeeId")} value={account.user_name} />
        <Row label={t("profile.email")} value={account.email_address} />
        <Row
          label={t("profile.club")}
          value={clubLabel(account.club) ?? t("profile.noClub")}
          muted={!account.club}
        />
        <Row
          label={t("profile.roles")}
          value={account.is_superuser ? t("profile.superuser") : roles}
        />
        {account.permissions ? (
          <Row
            label={t("profile.permissions")}
            value={t("profile.permissionsCount").replace(
              "{count}",
              String(account.permissions.length),
            )}
          />
        ) : null}
        {extra.map((row) => (
          <Row key={row.label} label={row.label} value={row.value} muted={row.muted ?? false} />
        ))}
      </Section>

      <Section
        title={t("profile.sectionPosting")}
        badge={
          account.is_temporary_club
            ? account.has_active_temporary_club
              ? t("profile.postingActive")
              : t("profile.postingScheduled")
            : undefined
        }
      >
        {account.is_temporary_club ? (
          <>
            <Row label={t("profile.postingClub")} value={clubLabel(account.temporary_club)} />
            <Row label={t("profile.postingFrom")} value={day(account.temporary_start_date)} />
            <Row label={t("profile.postingUntil")} value={day(account.temporary_end_date)} />
            <Row label={t("profile.postingReason")} value={account.temporary_club_reason} />
          </>
        ) : (
          <p className="text-fg-muted text-callout px-4 py-3">{t("profile.postingNone")}</p>
        )}
      </Section>

      <Section title={t("profile.sectionSecurity")}>
        <div className="grid lg:grid-cols-2 lg:gap-x-8">
          <Row
            label={t("profile.status")}
            value={t(`status.${account.status}` as TranslationKey)}
          />
          <Row label={t("profile.lastLogin")} value={when(account.last_login_at)} />
          <Row
            label={t("profile.passwordChanged")}
            value={when(account.last_password_change_at)}
          />
          <Row
            label={t("profile.forceChange")}
            value={
              account.force_password_change
                ? t("profile.forceChangeYes")
                : t("profile.forceChangeNo")
            }
          />
          <Row
            label={t("profile.openRequest")}
            value={
              account.is_password_request
                ? `${account.request_reason ?? ""} · ${when(account.password_request_at)}`
                : t("profile.openRequestNone")
            }
            muted={!account.is_password_request}
          />
          <Row label={t("profile.created")} value={when(account.created_at)} />
          <Row label={t("profile.updated_at")} value={when(account.updated_at)} />
        </div>
      </Section>
    </>
  );
}

/** One group of facts: a titled band, then the rows. */
function Section({
  title,
  badge,
  children,
}: {
  title: string;
  badge?: string | undefined;
  children: ReactNode;
}) {
  return (
    <section className="border-line border-t">
      <div className="bg-surface-inset/60 flex items-center justify-between gap-3 px-5 py-2.5 sm:px-6">
        <h2 className="text-fg text-subhead font-semibold tracking-wide">{title}</h2>
        {badge ? (
          <span className="bg-accent-soft text-accent text-footnote rounded-full px-2.5 py-0.5 font-medium">
            {badge}
          </span>
        ) : null}
      </div>
      <dl className="px-1 py-2 sm:px-2">{children}</dl>
    </section>
  );
}

function Row({
  label,
  value,
  muted = false,
}: {
  label: string;
  value: ReactNode;
  muted?: boolean;
}) {
  return (
    <div className="grid gap-0.5 px-4 py-2.5 sm:grid-cols-[11rem_minmax(0,1fr)] sm:gap-4">
      <dt className="text-fg-muted text-callout">{label}</dt>
      <dd className={`text-callout min-w-0 break-words ${muted ? "text-fg-muted" : "text-fg"}`}>
        {value ?? <span className="text-fg-muted">—</span>}
      </dd>
    </div>
  );
}

/**
 * The account's status, as a badge with a dot as well as a colour.
 *
 * Only `active` is green. Everything else is a reason someone cannot sign in,
 * and they are not interchangeable — `password_expired` clears itself with a
 * reset, `inactive` needs an administrator — so the word carries the meaning
 * and the colour only draws the eye.
 */
export function StatusPill({ status }: { status: AccountLike["status"] }) {
  const t = useTranslation();
  const tone =
    status === "active"
      ? "success"
      : status === "password_expired"
        ? "warning"
        : status === "deleted"
          ? "neutral"
          : "danger";
  return <Badge tone={tone}>{t(`status.${status}` as TranslationKey)}</Badge>;
}
